"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Input } from "../../ui/input";
import { ArrowLeft, Loader2, MessageSquare, Search } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface BAPBIPETestingProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface ExcelColumn {
  name: string;
  value: string;
}

const FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "asset_category", label: "Asset Category" },
  { key: "opening_balance_value", label: "Opening Balance (value)" },
  { key: "opening_balance_qty", label: "Opening Balance (quantity)" },
  { key: "acquisitions_value", label: "Acquisitions (value)" },
  { key: "acquisitions_qty", label: "Acquisitions (quantity)" },
  { key: "sale_disposal_value", label: "Sale or Disposal (mortality) (value)" },
  { key: "sale_disposal_qty", label: "Sale or Disposal (mortality) (quantity)" },
  { key: "closing_balance_value", label: "Closing Balance (value)" },
  { key: "closing_balance_qty", label: "Closing Balance (quantity)" },
];

export default function BAPBIPETesting({ onBack }: BAPBIPETestingProps) {
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");

  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `BAPB_IPE_TESTING_${normalizedSection}`;
  };

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_BAPB_BioAst");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  useEffect(() => {
    searchForFiles();
  }, []);

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(",")[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const fyYear = getFY_YEAR();
              if (!fyYear) {
                throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
              }

              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: fyYear,
                sectionList: ["Execution_BAPB_BioAst"],
              });

              if (result.success) {
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({
            name: col,
            value: col,
          }));
          setExcelColumns(columns);
        }
      }
    } catch {
      setExcelColumns([]);
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const handleMappingChange = (fieldKey: string, columnName: string) => {
    setColumnMappings((prev) => ({ ...prev, [fieldKey]: columnName }));
  };

  const allFieldsMapped = FIELD_KEYS.every(
    (f) => columnMappings[f.key] && columnMappings[f.key].length > 0
  );

  const runProcedure = async () => {
    if (!selectedFile || !allFieldsMapped) return;
    setProcessingStatus("running");
    try {
      if (!window.payroll?.run) {
        console.error("window.payroll.run is not available");
        setProcessingStatus("error");
        return;
      }

      const columnMap: Record<string, string> = {};
      FIELD_KEYS.forEach((field) => {
        columnMap[field.key] = columnMappings[field.key];
      });

      const options = {
        input_file: selectedFile,
        input_folder: "client",
        output_folder: "juggernaut",
        output_file: "Execution_BAPB_BioAst.json",
        column_map: columnMap,
      };

      const result = await window.payroll.run("execute_bapb_ipe_testing", {
        inputFiles: [],
        options,
      });
      if (result?.ok !== false) {
        setProcessingStatus("completed");
      } else {
        setProcessingStatus("error");
      }
    } catch (error) {
      console.error("Error running BAPB IPE Testing procedure:", error);
      setProcessingStatus("error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            IPE Testing – Biological Assets other than Bearer Plants
          </h2>
          <p className="text-gray-400">
            Map columns from the biological assets register for IPE testing
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setReviewCommentQuestionId(
                  getSectionQuestionId("Step 1: Search for Existing Files")
                )
              }
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
            {fileSearchStatus === "idle" && (
              <span className="text-gray-400">Searching for files...</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedFile && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={loadExcelColumns}
                    disabled={!selectedFile || isLoadingExcelColumns}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isLoadingExcelColumns ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading
                        Columns...
                      </>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && (
                    <span className="text-green-400 text-sm">
                      {" "}
                      {excelColumns.length} columns loaded
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">
                File not found. Please upload a file.
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button
                  onClick={uploadFile}
                  disabled={uploadStatus === "uploading"}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {uploadStatus === "uploading" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
                    </>
                  ) : (
                    "Upload File"
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">
                    File uploaded successfully!
                  </span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              Step 2: Map Columns from Biological Assets Register
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELD_KEYS.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label className="text-white">{field.label}</Label>
                <Select
                  value={columnMappings[field.key] || ""}
                  onValueChange={(value) => handleMappingChange(field.key, value)}
                  disabled={excelColumns.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        excelColumns.length === 0
                          ? "Load columns first"
                          : "Select column"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="pt-2 flex items-center justify-end">
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={!selectedFile || !allFieldsMapped || processingStatus === "running"}
              type="button"
              onClick={runProcedure}
            >
              {processingStatus === "running" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                "Run Procedure"
              )}
            </Button>
            {processingStatus === "completed" && (
              <span className="ml-3 text-sm text-green-400">
                Procedure completed successfully.
              </span>
            )}
            {processingStatus === "error" && (
              <span className="ml-3 text-sm text-red-400">
                Failed to run procedure. Check logs.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={reviewCommentQuestionId}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
